import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormControl, FormControl } from '@angular/forms';
import {
    SharedModule,
    StringCustomFieldConfig,
    FormInputComponent,
    NotificationService,
    DataService,
    Product
} from '@vendure/admin-ui/core';
import { safeJSONParse } from '../utils';
import { defaultProductReviewsData, PageType, ProductReviewsData, ProductSourceType } from '../ui-types';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'vdr-product-review-form',
  template: `
    <vdr-card>
      <vdr-form-field [label]="'Background Color'" class="mb-2">
          <input type="color" [formControl]="backgroundColorControl">
      </vdr-form-field>

      <div class="clr-row mb-2">
        <vdr-form-field [label]="'Product Source'" class="clr-col-12">
          <select [formControl]="productSourceControl">
            <option *ngIf="canUseInheritedProduct" value="dynamic">Dynamic</option>
            <option value="product">Product</option>
          </select>
        </vdr-form-field>
        <div *ngIf="productSourceControl.value === 'dynamic'" class="clr-col-12 mt-1">
          <p class="hint-text">Automatically displays the product reviews from the product currently being viewed by the customer.</p>
        </div>
      </div>

      <div *ngIf="productSourceControl.value === 'product'" class="clr-row">
        <vdr-form-field [label]="'Product'" class="clr-col-12">
          <vdr-dynamic-form-input
              [control]="productControl"
              [def]="{ type: 'relation', entity: 'Product', ui: { component: 'relation-form-input' } }">
          </vdr-dynamic-form-input>
        </vdr-form-field>
      </div>
    </vdr-card>
  `,
  styleUrls: ['../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class ProductReviewFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  backgroundColorControl = new FormControl();
  productSourceControl = new FormControl<Extract<ProductSourceType, 'dynamic' | 'product'>>('product');
  productControl = new UntypedFormControl();

  productReviewsData: ProductReviewsData;
  pageType: PageType;

  private destroyed$ = new Subject<void>();

  get canUseInheritedProduct(): boolean {
    return this.pageType === PageType.Product || this.pageType === PageType.ProductSpecific;
  }

  constructor(
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private dataService: DataService
  ) {}

  ngOnInit() {
    this.productReviewsData = safeJSONParse<ProductReviewsData>(
        this.formControl.value,
        this.notificationService,
        defaultProductReviewsData,
    )!;
    this.pageType = this.config.ui?.options?.pageType || PageType.General;
    this.initializeFormControls();
    this.handlePageTypeChange();
    this.config.ui?.changes?.pipe(takeUntil(this.destroyed$)).subscribe((data: PageType) => {
      if (data && data !== this.pageType) {
        this.pageType = data;
        this.handlePageTypeChange();
        this.changeDetector.detectChanges();
      }
    });

    if (this.productReviewsData?.productId) {
      this.dataService.product.getProduct(this.productReviewsData.productId.toString())
        .mapSingle(data => data.product).pipe(takeUntil(this.destroyed$)).subscribe(product => {
          this.productControl.setValue(product);
        });
    }
  }

  ngAfterViewInit() {
    this.backgroundColorControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onBackgroundColorChange);
    this.productSourceControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductSourceChange);
    this.productControl.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(this.onProductChange);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initializeFormControls() {
    this.backgroundColorControl.setValue(this.productReviewsData?.backgroundColor || '#ffffff');
    const sourceType = this.getSourceTypeFromData();
    this.productSourceControl.setValue(sourceType, { emitEvent: false });
  }

  private getSourceTypeFromData(): Extract<ProductSourceType, 'dynamic' | 'product'> {
    if (this.productReviewsData?.sourceType) {
      return this.productReviewsData.sourceType;
    }
    return this.productReviewsData?.productId ? 'product' : 'dynamic';
  }

  private handlePageTypeChange() {
    if (this.productSourceControl.value === 'dynamic' && !this.canUseInheritedProduct) {
      this.productSourceControl.setValue('product', { emitEvent: false });
      this.productReviewsData.sourceType = 'product';
      this.updateJsonString();
    }
  }

  private onBackgroundColorChange = (value: string) => {
    if (this.productReviewsData?.backgroundColor !== value) {
      this.productReviewsData!.backgroundColor = value;
      this.updateJsonString();
    }
  };

  private onProductChange = (product: Product) => {
    const productId = product ? product.id : null;
    if (this.productReviewsData?.productId !== productId) {
      this.productReviewsData!.productId = productId;
      this.updateJsonString();
    }
  };

  private onProductSourceChange = (value: Extract<ProductSourceType, 'dynamic' | 'product'> | null) => {
    const sourceType = value === 'dynamic' && !this.canUseInheritedProduct ? 'product' : (value ?? 'product');
    if (this.productReviewsData?.sourceType !== sourceType) {
      this.productReviewsData!.sourceType = sourceType;
      if (sourceType === 'dynamic') {
        this.productControl.setValue(null, { emitEvent: false });
        this.productReviewsData!.productId = null;
      }
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    if (!this.canUseInheritedProduct && this.productReviewsData.sourceType === 'dynamic') {
      this.productReviewsData.sourceType = 'product';
    }
    const jsonString = JSON.stringify(this.productReviewsData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
